import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Copy, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Applications() {
  const [apps, setApps] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [reasons, setReasons] = useState({});
  const [links, setLinks] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/applications").then((r) => setApps(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const review = async (a, decision) => {
    if (decision === "reject" && !window.confirm(`Reject ${a.name}'s application?`)) return;
    setBusy(a.id);
    try {
      const r = await api.post(`/admin/applications/${a.id}/review`, { decision, reason: reasons[a.id] || "" });
      if (decision === "approve") toast.success(r.data.email_sent ? `Approved — set-password email sent to ${a.email}` : "Approved — email failed, copy the link below");
      else toast.success("Application rejected");
      if (r.data.setup_link) setLinks((l) => ({ ...l, [a.id]: r.data.setup_link }));
      load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(""); }
  };

  const shown = (apps || []).filter((a) => filter === "all" || a.status === filter);
  const pendingCount = (apps || []).filter((a) => a.status === "pending").length;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14" data-testid="applications-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-700 mb-6" data-testid="applications-back-link"><ArrowLeft className="w-4 h-4" /> Back to tutorials</Link>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow">Admin panel</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-2">Clinician applications</h1>
          <p className="text-slate-600 mt-2">Check the license on the state board, then approve or reject. Approved clinicians receive a set-password email.</p>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-full p-1">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button key={s} type="button" onClick={() => setFilter(s)} data-testid={`applications-filter-${s}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${filter === s ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
              {s}{s === "pending" && pendingCount > 0 ? ` (${pendingCount})` : ""}
            </button>
          ))}
        </div>
      </div>

      {!apps ? <Skeleton className="h-64 rounded-3xl" /> : shown.length === 0 ? (
        <p className="rounded-3xl border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="applications-empty">No {filter === "all" ? "" : filter} applications.</p>
      ) : (
        <ul className="space-y-4" data-testid="applications-list">
          {shown.map((a) => (
            <li key={a.id} className="rounded-3xl bg-white border border-slate-200 p-6 space-y-4" data-testid={`application-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-display text-lg font-bold text-slate-900">{a.name} <span className="text-sm font-normal text-slate-500">· {a.email}{a.phone ? ` · ${a.phone}` : ""}</span></p>
                  <p className="text-sm text-slate-600 mt-1"><strong>{a.license_type}</strong> · License # <span className="font-mono">{a.license_number}</span> · {a.license_state}{a.practice_name ? ` · ${a.practice_name}` : ""}</p>
                  {a.notes && <p className="text-sm text-slate-500 mt-2 italic">"{a.notes}"</p>}
                  <p className="text-xs text-slate-400 mt-2">Applied {fmt(a.created_at)}{a.reviewed_at ? ` · Reviewed ${fmt(a.reviewed_at)}` : ""}</p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${a.status === "pending" ? "bg-amber-50 text-amber-700 border border-amber-200" : a.status === "approved" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-600"}`} data-testid={`application-status-${a.id}`}>{a.status}</span>
              </div>
              {a.status === "pending" ? (
                <div className="space-y-3">
                  <a href={a.lookup_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800" data-testid={`lookup-${a.id}`}>Open {a.license_state} license lookup <ExternalLink className="w-3.5 h-3.5" /></a>
                  <Textarea value={reasons[a.id] || ""} onChange={(e) => setReasons((r) => ({ ...r, [a.id]: e.target.value }))} rows={2} placeholder="Optional note (included in a rejection email)" className="rounded-xl" data-testid={`reason-${a.id}`} />
                  <div className="flex gap-2">
                    <Button onClick={() => review(a, "approve")} disabled={busy === a.id} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${a.id}`}><Check className="w-4 h-4" /> Approve</Button>
                    <Button onClick={() => review(a, "reject")} disabled={busy === a.id} variant="outline" className="rounded-full text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`reject-${a.id}`}><X className="w-4 h-4" /> Reject</Button>
                  </div>
                </div>
              ) : a.review_reason ? <p className="text-sm text-slate-500">Note: {a.review_reason}</p> : null}
              {links[a.id] && (
                <p className="text-xs text-indigo-700 flex items-center gap-2 break-all" data-testid={`setup-link-${a.id}`}>Set-password link: <span className="truncate">{links[a.id]}</span><button type="button" onClick={() => { navigator.clipboard?.writeText(links[a.id]); toast.success("Copied"); }} aria-label="Copy"><Copy className="w-3 h-3" /></button></p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
